export type Maybe<T> = T | null;

export type TimeStamp = number;

/** A graphic is usually the string src representation of an image upload */
export type Graphic = Maybe<string>;

export enum Status {
  ACTIVE = "active",
  INACTIVE = "inactive",
  ON_HOLD = "on_hold",
  OUT_LATE = "out_late",
  CLOSED_TEMPORARILY = "closed_temporarily",
  CLOSED_SEASONALLY = "closed_seasonally",
  CLOSED_PERMANENTLY = "closed_permanently"
}

export interface Statused {
  /** Status when no override is active */
  canonicalStatus: Status;
  /** Text note when status has not been overridden */
  canonicalStatusNote: string;
  /** Temporary status override */
  overrideStatus: Status | null;
  /** Text note when status has been overriden */
  overrideStatusNote: string | null;
  /** While this Date is set and in the future, the status will be overriden */
  overrideStatusExpiration: Date | null;
}

export enum Weekday {
  Sunday = "sunday",
  Monday = "monday",
  Tuesday = "tuesday",
  Wednesday = "wednesday",
  Thursday = "thursday",
  Friday = "friday",
  Saturday = "saturday"
}

export type Point = {
  x: number;
  y: number;
};

export interface Announcement {
  id: string;
  message: string;
  icon: Maybe<Icon>;
  iconColor: string;
  iconSrc: string;
  rank: number;
  onSchedule: boolean;
  onScreens: boolean;
  onWelcome: boolean;
}

export interface Asset {
  id: string;
  name: string;
  alt: string;
  src: string;
  type: string;
}

export enum EventType {
  Event = "event",
  Close = "park-close",
  Open = "park-open"
}

/**
 * Activity for a facility. This is separate from EventHours,
 * describing when an event takes place.
 */
export interface Event {
  id: string;
  description: string;
  graphic: Graphic;
  schedule: Array<EventHours>;
  title: string;
  type: EventType;
}

export interface Hours {
  startTime: string;
  endTime: string;
  startDate: Maybe<Date>;
  endDate: Maybe<Date>;
  weekdays: Array<Weekday>;
  overrides?: Array<EventHoursOverride>;
}

export interface EventHours extends Hours {
  id: string;

  event: Event;
  eventId: string;

  place?: Maybe<Place>;
  placeId?: Maybe<string>;

  overrides: Array<EventHoursOverride>;
}

export interface EventHoursOverride {
  id: string;
  date: Date;
  startTime: string;
  endTime: string;
  cancelled: boolean;
}

export interface EventHoursOccurrence extends Occurrence {
  id: string;

  event: Event;
  eventId: string;

  place: Maybe<Place>;
  placeId: Maybe<string>;

  from: Date;
  until: Date;
}

export interface Font {
  id: string;
  fontName: string;
  fontWeight: number;
  preconnectUrl: Maybe<string>;
  resourceUrl: Maybe<string>;
}

export interface HeraldPage {
  id: string;
  title: string;
  url: string;

  icon: Maybe<Icon>;
  iconSrc: Maybe<string>;
}

/** Icons are graphical elements associated with other resources. */
export interface Icon {
  id: string;
  name: string;
  src: string;
}

export interface Map {
  id: string;
  title: string;

  backgroundColor: string;
  connectionColor: string;
  pinColor: Maybe<string>;
  textColor: string;
  textStrokeColor: Maybe<string>;
  waypointColor: string;

  template: Maybe<MapTemplate>;
  textboxes: Array<MapTextbox>;
  waypoints: Array<MapWaypoint>;
  icons: Array<MapIcon>;

  /** All fonts referenced by shapes on a map. */
  fonts: Array<Font>;
  /** All places referenced by shapes on the map. */
  places: Array<Place>;
}

export interface MapIcon {
  id: string;
  /** Color of the icon. Fills the entire icon. */
  color: string;
  /** 2D point representing position on map. */
  position: Point;
  /** Rotation along the z axis of the icon */
  rotation: number;
  /** Pixel size of icon at 100% zoom. */
  size: number;
  src: string;
  /** Alias for x coordinate in position. */
  x: number;
  /** Alias for y coordinate in position. */
  y: number;

  icon: Maybe<Icon>;
  iconId: string;

  map: Map;
  mapId: string;
}

export interface MapTemplate {
  id: string;
  name: string;
  rotation: number;
  height: number;
  width: number;

  geopoints: Array<MapTemplateGeopoint>;

  layers: Array<MapTemplateLayer>;
  layerImages: Array<string>;
}

export interface MapTemplateGeopoint {
  id: string;
  geopoint: Point;
  position: Point;

  mapTemplate: Maybe<MapTemplate>;
  mapTemplateId: Maybe<string>;
}

export interface MapTemplateLayer {
  id: string;
  name: string;
  image: string;
  rank: number;

  mapTemplate: Maybe<MapTemplate>;
  mapTemplateId: Maybe<string>;
}

export interface MapTextbox {
  id: string;
  color: string;
  /** 2D point representing position on map. */
  rotation: number;
  text: string;
  textAlign: string;
  width: number;
  zoomMax: number;
  zoomMin: number;

  position: Point;
  /** Alias for x coordinate in position. */
  x: number;
  /** Alias for y coordinate in position. */
  y: number;

  font: Maybe<Font>;
  fontFamily: string;
  fontId: string;
  fontSize: number;
  fontWeight: number;
  fontWeightOffset: number;

  icon: Maybe<Icon>;
  iconColor: string;
  iconId: string;
  iconOffset: Point;
  iconSize: number;
  iconSrc: string;

  map: Map;
  mapId: string;

  place: Maybe<Place>;
  placeId: Maybe<string>;
}

export interface MapWaypoint {
  id: string;
  gravity: number;
  hazard: boolean;
  rotation: number;
  shape: string;
  radius: number;

  /** 2D point representing position on map. */
  position: Point;
  /** Alias for x coordinate in position. */
  x: number;
  /** Alias for y coordinate in position. */
  y: number;

  map?: Map;
  mapId?: string;

  place?: Maybe<Place>;
  placeId?: Maybe<string>;
}

export interface Occurrence {
  from: Date;
  until: Date;
}

export interface Page {
  id: string;
  name: string;
  backgroundColor: string;
  textColor: string;
  layout: string;
  pageLinks: Array<PageLink>;
}

export interface PageLink {
  id: string;
  name: string;
  backgroundColor: string;
  enabled: boolean;
  rank: number;
  showLabel: boolean;
  textColor: string;
  thumbnail: string;
  url: string;

  page: Page;
  tenant: Tenant;
}

export interface PlaceType {
  id: string;
  label: string;
  description: string;
  note: string;

  parentId: Maybe<string>;

  children?: Array<PlaceType>;

  icon: Maybe<Icon>;
  iconColor: Maybe<string>;
  iconSrc: Maybe<string>;

  places: Array<Place>;
  placeIds: string[];
  showPlaceParents: boolean;
}

export interface Place extends Statused {
  id: string;
  title: string;
  description: string;
  graphic: Graphic;

  map?: Maybe<Map>;
  mapId?: string;

  parent?: Maybe<Place>;
  parentTitle?: Maybe<string>;

  placeHoursGroupId?: Maybe<string>;
  placeHoursGroup?: Maybe<PlaceHoursGroup>;
  /** Indicates if start time is uncertain or ambiguous. */
  startTimeIsAmbiguous?: boolean;
  /** Indicates if end time is uncertain or ambiguous. */
  endTimeIsAmbiguous?: boolean;

  parentId?: Maybe<string>;
}

export interface PlaceHours extends Hours {
  id: string;
}

export interface PlaceHoursGroup {
  id: string;
  name: string;
  description: string;
  placeHours: Array<PlaceHours>;
}

export interface Schedule {
  id: string;
  eventHours: Array<EventHours>;
  title: string;
}

export interface Settings {
  /** Description used by search engines via the meta tag description field. */
  heraldDescription: string;
  /** Google Tag Manager Container ID */
  heraldGtmContainerId: Maybe<string>;
  /** Large take over graphic used for splash screens and social media sharing. */
  heraldHeroGraphic: Graphic;
  /** Page used by the Herald mobile app. */
  heraldHomePage: Maybe<Page>;
  heraldHomePageId: Maybe<string>;
  /** App icon used for adding Herald to users' homescreens. */
  heraldIconGraphic: Graphic;
  /**
   * Allow users to set their location (testing mode)
   * @deprecated Removed for UX reasons. It will always return false.
   *
   */
  heraldManualLocation: boolean;
  /** Map used by the Herald mobile app. */
  heraldMap: Maybe<Map>;
  heraldMapId: Maybe<string>;
  /** Name of app for search engines and progressive web app installation. */
  heraldName: string;
  /** Schedule used by the Herald mobile app. */
  heraldSchedule: Maybe<Schedule>;
  heraldScheduleId: Maybe<string>;
  /** Show end times when presented in schedule */
  heraldShowEventEndTime: boolean;
  /** Theme color for Herald app. Appears in Google Chrome title bar. */
  heraldThemeColor: string;
  /** Tenant identifier */
  tenantId: string;
}

export interface Guide {
  id: string;
  title: string;
  description: string;
  coverColor: string;
  coverImage: string;
  /** Suggested text color based on cover color. */
  coverTextColor: string;
  includeIndexPage: boolean;
  indexDescription: string;
  indexTitle: string;
  spots: Array<Spot>;
}

export interface Spot {
  id: string;
  title: string;
  description: string;
  isCompletable: boolean;
  position: Maybe<Point>;
  rank: number;
  showMap: boolean;

  guide: Guide;

  map: Maybe<Map>;
  mapId: Maybe<string>;
}

export interface Tenant {
  id: string;
  name: string;
  features: Array<string>;
  mobileJavascript: string;
  plausibleDomain: string;
  timeZone: string;
  whiteLabel: string;
}

// Geolocation

export type Location = {
  accuracy: number;
  latitude: number;
  longitude: number;
  timestamp: TimeStamp;
};

// Layout Settings

export type Theme = {
  navBackgroundColor: string;
  navSelectedBackgroundColor: string;

  navIconColor: string;
  navSelectedIconColor: string;

  homeIconSrc: string;
  homeSelectedIconSrc: string;
  mapIconSrc: string;
  mapSelectedIconSrc: string;
  scheduleIconSrc: string;
  scheduleSelectedIconSrc: string;
  infoIconSrc: string;
  infoSelectedIconSrc: string;
  moreIconSrc: string;
  moreSelectedIconSrc: string;
};

export type LayoutSettings = {
  theme: Maybe<Theme>;
  name: string;
  description: string;
  favicon: Graphic;
  touchIcon: Graphic;
  hero: Graphic;
  gtmContainerId: Maybe<string>;
  gaMeasurementId: Maybe<string>;
  showSchedule: boolean;
  showEventEndTime: boolean;
  showHome: boolean;
  showMap: boolean;
  tenant: string;

  activeStatusLabel: Maybe<string>;
  inactiveStatusLabel: Maybe<string>;
  onHoldStatusLabel: Maybe<string>;
  outLateStatusLabel: Maybe<string>;
  closedTemporarilyStatusLabel: Maybe<string>;
  closedSeasonallyStatusLabel: Maybe<string>;
  closedPermanentlyStatusLabel: Maybe<string>;

  homeTitle: Maybe<string>;
  homeDescription: Maybe<string>;
  mapTitle: Maybe<string>;
  mapDescription: Maybe<string>;
  scheduleTitle: Maybe<string>;
  scheduleDescription: Maybe<string>;
  infoTitle: Maybe<string>;
  infoDescription: Maybe<string>;
  moreTitle: Maybe<string>;
  moreDescription: Maybe<string>;
};
